import React, { useState } from 'react';
import { backendService } from '../services/backendService';
import { SpinnerIcon, CloseIcon } from './Icons';
import './MemberRestriction.css';

interface MemberRestrictionProps {
  onVerified: (phone: string) => void;
  onClose: () => void;
}

const MemberRestriction: React.FC<MemberRestrictionProps> = ({ onVerified, onClose }) => {
  const [phone, setPhone] = useState('');
  const [isChecking, setIsChecking] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // 需要的产品ID列表
  const requiredProductIds = ['8367211004', '8367211007', '8367211008'];

  // 处理数字按钮点击
  const handleNumberClick = (num: string) => {
    if (phone.length < 11 && !isChecking) {
      setPhone(prev => prev + num);
      setErrorMessage('');
    }
  };

  // 处理删除按钮
  const handleDelete = () => {
    if (!isChecking) {
      setPhone(prev => prev.slice(0, -1));
      setErrorMessage('');
    }
  };

  // 处理清空按钮
  const handleClear = () => {
    if (!isChecking) {
      setPhone('');
      setErrorMessage('');
    }
  };

  const handleSubmit = async () => {
    // 验证手机号格式
    if (!phone || !/^1[3-9]\d{9}$/.test(phone)) {
      setErrorMessage('请输入有效的手机号码');
      return;
    }

    setIsChecking(true);
    setErrorMessage('');

    try {
      // 测试号码直接通过
      if (phone === '16602099162') {
        console.log('测试号码，直接通过会员验证');
        onVerified(phone);
        return;
      }

      // 调用后端接口检查会员类型
      const result = await backendService.getUserMemberType(phone);

      if (result.success && result.data) {
        // 检查是否有符合条件的会员
        const hasValidMembership = result.data.some((member) => {
          // 检查该会员的产品ID是否包含需要的任一产品ID
          return member.subProductIds.some((productId) => 
            requiredProductIds.includes(productId)
          );
        });

        if (hasValidMembership) {
          console.log('会员验证通过');
          const availableUsageCount = 2 - (result.usageCount || 0);
          if (availableUsageCount <= 0) {
            setErrorMessage('会员本月使用次数已用完，请续费后再试');
            return;
          }
          onVerified(phone);
        } else {
          setErrorMessage('该手机号未订购相应业务');
        }
      } else {
        setErrorMessage('会员验证失败，请稍后重试');
      }
    } catch (error) {
      console.error('会员验证出错:', error);
      setErrorMessage('验证过程中出现错误，请稍后重试');
    } finally {
      setIsChecking(false);
    }
  };

  return (
    <div className="member-restriction-overlay">
      <div className="member-restriction-dialog">
        <button className="dialog-close-button" onClick={onClose} aria-label="关闭">
          <CloseIcon width="24" height="24" />
        </button>

        <div className="dialog-content">
          {/* 左侧：显示区域 */}
          <div className="display-area">
            <h2 className="dialog-title">会员验证</h2>
            <p className="dialog-description">
              此功能仅限会员使用，请输入您的手机号进行验证
            </p>

            <div className="phone-display">
              <div className="phone-label">手机号码</div>
              <div className="phone-number">
                {phone || <span className="placeholder">请输入手机号</span>}
              </div>
            </div>

            {errorMessage && (
              <div className="error-message">
                {errorMessage}
              </div>
            )}

            <div className="dialog-actions">
              {errorMessage ? (
                <button
                  type="button"
                  className="button button-md button-primary"
                  onClick={() => setErrorMessage('')}
                >
                  确认
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    className="button button-md button-secondary"
                    onClick={onClose}
                    disabled={isChecking}
                  >
                    取消
                  </button>
                  <button
                    type="button"
                    className="button button-md button-primary"
                    onClick={handleSubmit}
                    disabled={isChecking || phone.length !== 11}
                  >
                    {isChecking ? (
                      <>
                        <SpinnerIcon width="24" height="24" />
                        验证中...
                      </>
                    ) : (
                      '提交验证'
                    )}
                  </button>
                </>
              )}
            </div>
          </div>

          {/* 右侧：数字键盘 */}
          <div className="numpad-area">
            <div className="numpad-grid">
              {[1, 2, 3, 4, 5, 6, 7, 8, 9].map(num => (
                <button
                  key={num}
                  className="numpad-button"
                  onClick={() => handleNumberClick(num.toString())}
                  disabled={isChecking}
                >
                  {num}
                </button>
              ))}
              <button
                className="numpad-button numpad-clear"
                onClick={handleClear}
                disabled={isChecking}
              >
                清空
              </button>
              <button
                className="numpad-button"
                onClick={() => handleNumberClick('0')}
                disabled={isChecking}
              >
                0
              </button>
              <button
                className="numpad-button numpad-delete"
                onClick={handleDelete}
                disabled={isChecking}
              >
                ⌫
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MemberRestriction;
